import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { SpaceCollectionListPlaceholder } from '@gem/components';
import { IcPlusIcon, IcSDone16Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { customScrollStyles, PopupLink } from '@gem/ui-kit';

import { Avatar, AvatarTypes } from 'APP/components/Avatar/Avatar';
import { Space } from 'STORE/Spaces/Space';

import {
  ISpaceCollectionData,
  useSpaceCollectionListPresenter,
} from './SpaceCollectionList.presenter';
import styles from './SpaceCollectionList.styles.m.css';

interface ISpaceCollectionListProps {
  space: Space;
  groupId: string;
  messageId: string;
  onCreateNewCollectionStep(): void;
  onSelectCollection(collection: ISpaceCollectionData): void;
}

export const SpaceCollectionList: FC<ISpaceCollectionListProps> = observer(
  ({ space, groupId, messageId, onCreateNewCollectionStep, onSelectCollection }) => {
    const { t } = useTranslation();
    const presenter = useSpaceCollectionListPresenter({ space, groupId, messageId });

    return (
      <div className={classNames(styles.container, customScrollStyles.container)}>
        <PopupLink
          className={styles.createButton}
          title={t('space_create_new_collection_button')}
          Icon={IcPlusIcon}
          onClick={onCreateNewCollectionStep}
        />

        {!presenter.isLoading && (
          <>
            {presenter.collections.map((collection) => (
              <div
                className={styles.collection}
                key={collection.id}
                role="button"
                tabIndex={0}
                onClick={(): void => onSelectCollection(collection)}
              >
                <div className={styles.avatar}>
                  <Avatar
                    type={AvatarTypes.Square_20}
                    url={collection.avatarUrl}
                  />

                  {!collection.canAddMessage && (
                    <div className={styles.badge}>
                      <IcSDone16Icon className={styles.badgeIcon} />
                    </div>
                  )}
                </div>

                <span className={styles.name}>{collection.name}</span>
              </div>
            ))}
          </>
        )}

        {presenter.isLoading && <SpaceCollectionListPlaceholder className={styles.placeholders} />}
      </div>
    );
  }
);
